import { InsightsData } from "../../hooks/useInsights";
import "./KeyStatistics.css";

interface KeyStatisticsProps {
  statistics: InsightsData["statistics"];
  comparisons: InsightsData["comparisons"]["vsPreviousPeriod"];
}

const ARROWS = {
  up: "↑",
  down: "↓",
  flat: "→",
};

export function KeyStatistics({
  statistics,
  comparisons,
}: KeyStatisticsProps) {
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-US", {
      timeZone: "America/Halifax",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const stats = [
    {
      label: "Average Daily Demand",
      data: statistics.avgDailyDemand,
      comparisonKey: "avgDailyDemand" as const,
      positiveIsGood: null,
    },
    {
      label: "Peak Demand",
      data: statistics.peakDemand,
      showTimestamp: true,
      comparisonKey: "peakDemand" as const,
      positiveIsGood: null,
    },
    {
      label: "Total Customers Affected",
      data: statistics.totalCustomersAffected,
      comparisonKey: "totalCustomersAffected" as const,
      positiveIsGood: false,
      hideUnitInSecondary: true,
    },
    {
      label: "Outage Days",
      data: statistics.outageDays,
      comparisonKey: "outageCount" as const,
      positiveIsGood: false,
      hideUnitInSecondary: true,
    },
  ].filter((s) => s.data !== null);

  const getChangeClass = (direction, positiveIsGood) => {
    if (positiveIsGood === null) return "";
    if (direction === "up")
      return positiveIsGood
        ? "KeyStatistics-change--positive"
        : "KeyStatistics-change--negative";
    if (direction === "down")
      return positiveIsGood
        ? "KeyStatistics-change--negative"
        : "KeyStatistics-change--positive";
    return "";
  };

  if (stats.length === 0) {
    return null;
  }

  return (
    <div className="KeyStatistics">
      <div className="KeyStatistics-grid">
        {stats.map((stat) => {
          const comparison = comparisons[stat.comparisonKey];
          return (
            <div key={stat.label} className="KeyStatistics-stat">
              <div className="KeyStatistics-label">{stat.label}</div>
              <div className="KeyStatistics-value">
                <span className="KeyStatistics-valueText">
                  {stat.data!.value.toLocaleString()}
                </span>
                <span className="KeyStatistics-unit">{stat.data!.unit}</span>
                {comparison && (
                  <span className="KeyStatistics-secondary">
                    <span
                      className={`KeyStatistics-secondaryValue ${getChangeClass(comparison.direction, stat.positiveIsGood)}`}
                    >
                      {ARROWS[comparison.direction]}{" "}
                      {comparison.change > 0 ? "+" : ""}
                      {comparison.change}
                      {!stat.hideUnitInSecondary && ` ${stat.data!.unit}`}
                    </span>
                  </span>
                )}
              </div>
              {stat.showTimestamp && stat.data!.timestamp && (
                <div className="KeyStatistics-timestamp">
                  {formatTimestamp(stat.data!.timestamp)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
